import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  CheckCircle,
  ArrowRight,
  User,
  Package,
  Receipt,
  Download,
  Rocket,
  ShieldCheck,
  ChevronRight
} from 'lucide-react';
import { LoginContext } from '../LoginContext';
import { useTranslation } from 'react-i18next';
import { getBaseUrl } from '../config/apiConfig';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginInfo, loading: loginLoading } = useContext(LoginContext);
  const [loading, setLoading] = useState(true);
  const [sessionDetails, setSessionDetails] = useState(null);
  const [paymentProcessed, setPaymentProcessed] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const sessionId = searchParams.get('session_id');
    const isTest = searchParams.get('test');

    if (isTest) {
      setSessionDetails({
        success: true,
        product: 'Test-Paket',
        amount: '0.00',
        downloads: 'Test',
        paymentStatus: 'paid'
      });
      setPaymentProcessed(true);
      setLoading(false);
    } else if (sessionId) {
      fetch(`${getBaseUrl()}/api/payment/check-session?session_id=${sessionId}`)
        .then(response => response.json())
        .then(data => {
          setSessionDetails(data);
          if (data.success) {
            setPaymentProcessed(true);
          }
          setLoading(false);
        })
        .catch(error => {
          console.error('Fehler beim Abrufen der Session-Details:', error);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [location]);

  if (loading || loginLoading) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-[#4fc3f7]/20 border-t-[#4fc3f7] rounded-full animate-spin" />
          <p className="text-slate-400 font-mono text-[10px] uppercase tracking-[0.3em]">Processing Secure Payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-100 font-sans selection:bg-[#4fc3f7]/30 flex flex-col items-center justify-center p-6 relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-20">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#4fc3f7]/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-500/10 blur-[120px] rounded-full" />
      </div>

      <div className="w-full max-w-2xl bg-[#1e293b]/50 backdrop-blur-2xl border border-[#334155] rounded-[3rem] p-8 md:p-12 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] relative overflow-hidden animate-in fade-in zoom-in duration-700">
        {/* Header Section */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-[#4fc3f7]/10 rounded-[2rem] border border-[#4fc3f7]/20 mb-8 animate-bounce-subtle">
            <CheckCircle className="text-[#4fc3f7]" size={48} />
          </div>
          <h1 className="text-3xl md:text-4xl font-black uppercase tracking-[0.2em] mb-4 text-white">
            {t('profile.Zahlung erfolgreich!', 'Payment Successful')}
          </h1>
          <p className="text-slate-400 text-sm md:text-base font-bold uppercase tracking-widest leading-relaxed max-w-md mx-auto">
            {t('profile.Vielen Dank für deine Bestellung! Deine Downloads wurden erfolgreich zu deinem Konto hinzugefügt.', 'Your order has been processed. Credits have been allocated to your account.')}
          </p>
        </div>

        {/* User Identity HUD */}
        <div className="bg-black/20 border border-white/5 rounded-2xl p-4 mb-8 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-slate-800 rounded-lg">
              <User size={16} className="text-slate-400" />
            </div>
            <div>
              <p className="text-[10px] text-slate-500 font-bold uppercase tracking-tight">Active Identity</p>
              <p className="text-xs font-mono font-bold text-[#4fc3f7]">{loginInfo?.username || 'ANONYMOUS_USER'}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2 px-3 py-1 bg-[#4fc3f7]/10 border border-[#4fc3f7]/20 rounded-full">
            <ShieldCheck size={12} className="text-[#4fc3f7]" />
            <span className="text-[9px] font-bold text-[#4fc3f7] uppercase tracking-tighter">Verified Session</span>
          </div>
        </div>

        {/* Order Details Grid */}
        {sessionDetails && sessionDetails.success && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="bg-slate-800/40 border border-white/5 rounded-[1.5rem] p-6 group hover:border-[#4fc3f7]/30 transition-all">
              <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mb-2 flex items-center">
                <Package size={12} className="mr-2" />
                Plan / Package
              </p>
              <p className="text-xl font-bold text-white tracking-wide">{sessionDetails.product}</p>
            </div>
            <div className="bg-slate-800/40 border border-white/5 rounded-[1.5rem] p-6 group hover:border-[#4fc3f7]/30 transition-all">
              <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mb-2 flex items-center">
                <Receipt size={12} className="mr-2" />
                Transaction Total
              </p>
              <p className="text-xl font-bold text-white tracking-wide">{sessionDetails.amount} €</p>
            </div>
            <div className="bg-slate-800/40 border border-white/5 rounded-[1.5rem] p-6 md:col-span-2 group hover:border-[#4fc3f7]/30 transition-all flex items-center justify-between">
              <div>
                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mb-1 flex items-center">
                  <Download size={12} className="mr-2" />
                  Allocated Download Credits
                </p>
                <p className="text-2xl font-black text-[#4fc3f7] tracking-tighter">+{sessionDetails.downloads} UNITS</p>
              </div>
              <div className="w-12 h-12 bg-[#4fc3f7]/5 rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-[#4fc3f7] rounded-full animate-ping" />
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => {
              if (!loginInfo?.username) {
                navigate('/login');
                return;
              }
              navigate('/profile?refresh=true');
            }}
            className="group relative h-16 bg-slate-800 hover:bg-slate-700 text-white rounded-[1.5rem] font-bold text-[11px] uppercase tracking-[0.2em] transition-all border border-[#334155] flex items-center justify-center space-x-3 overflow-hidden"
          >
            <User size={18} className="text-slate-400 group-hover:text-white transition-colors" />
            <span>{t('profile.Mein Profil ansehen', 'View Account Profile')}</span>
          </button>
          <button
            onClick={() => navigate('/upload')}
            className="group relative h-16 bg-[#4fc3f7] hover:bg-[#4fc3f7] text-white rounded-[1.5rem] font-bold text-[11px] uppercase tracking-[0.2em] transition-all shadow-xl shadow-[#4fc3f7]/20 flex items-center justify-center space-x-3 overflow-hidden"
          >
            <Rocket size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            <span>{t('profile.Neuen Scan starten', 'Initiate Workstation')}</span>
          </button>
        </div>

        {/* Footer info */}
        <div className="mt-10 text-center">
          <p className="text-[9px] font-mono text-slate-600 uppercase tracking-[0.4em] font-bold">
            Cloudron Secure Deployment // 256-bit TLS Protected
          </p>
        </div>
      </div>

      {/* Styles for bounce-subtle */}
      <style>{`
        @keyframes bounce-subtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-subtle {
          animation: bounce-subtle 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default PaymentSuccess;
